<template>
  <div>
    <v-card
      :class="$style.orders_widget"
      :loading="repair_types_loading"
    >
      <div class="d-flex flex-column mb-6">
        <span
          :class="$style.widget_title"
          class="display-3"
        >
          Соотношение типов ремонтов
        </span>
        <span v-if="!is_filters">за текущий месяц</span>
      </div>

      <template v-if="is_filters">
        <div class="d-flex mb-6">
          <v-row>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <DatepickerRange
                :date_prop="filters.repair_types_dates"
                label="Период создания"
                type_date='period'
                :class="[$style.datepicker, $style.filterField, $style.datepickerMargin]"
                dense
                clearable
                range
                small_info
                @date_changed="handle_change_dates"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.repair_types_organization_id"
                :items="orgs"
                :class="[$style.select, $style.filterField]"
                label="Организация"
                item-text="name_organization"
                item_value="id"
                multiple
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('repair_types_organization_id', $event, 'repair_types')"
              />
            </v-col>
            <v-col
              cols="12"
              xl="4"
              lg="6"
              md="12"
              sm="6"
              xs="12"
            >
              <Select
                :value="filters.repair_types_client_id"
                :items="clients"
                :class="[$style.select, $style.filterField]"
                label="Клиент"
                item-text="name_client_company"
                item_value="client_id"
                dense
                hide-details
                clearable
                blue_icon
                @input="filter_update('repair_types_client_id', $event, 'repair_types')"
              />
            </v-col>
          </v-row>
        </div>
      </template>

      <template v-if="is_filters">
        <div :class="$style.chart_container">
          <div
            class="d-flex justify-center"
            :style="{ marginTop: analytics_repair_types_count > 0 ? '0' : '36px' }"
          >
            <div
              v-if="analytics_repair_types_count > 0 && analytics_repair_types.length > 0"
              id="chart"
              :class="$style.chart_wrapper"
            >
              <apexchart
                type="donut"
                :options="chartOptions"
                :series="series"
                :class="$style.donut_size"
              />
            </div>
          </div>

          <div :class="$style.data_description">
            <div class="d-flex justify-space-between">
              <div>
                <span>Количество ремонтов</span>
              </div>
              <div>
                <span class="font-weight-bold">{{ analytics_repair_types_count }}</span>
              </div>
            </div>

            <div
              v-for="item in analytics_repair_types"
              :key="item.title"
              class="data_item"
            >
              <div :class="$style.leads_discription_items">
                <div :class="$style.leads_discription_item_i">
                  <div
                    :class="[$style.leads_discription_label_color]"
                    :style="{ backgroundColor: colors[analytics_repair_types.indexOf(item)] }"
                  />
                  <span class="ml-4">{{ item.title }}</span>
                </div>
                <div>
                  <span class="headline font-weight-bold">{{ item.value }} ({{ item.percent }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div :class="$style.chart_container">
          <div
            class="d-flex justify-center"
            :style="{ marginTop: repair_types_count > 0 ? '0' : '36px' }"
          >
            <div
              v-if="repair_types_count > 0 && repair_types.length > 0"
              id="chart"
              :class="$style.chart_wrapper"
            >
              <apexchart
                type="donut"
                :options="chartOptions"
                :series="series"
                :class="$style.donut_size"
              />
            </div>
          </div>

          <div :class="$style.data_description">
            <div class="d-flex justify-space-between">
              <div>
                <span>Количество ремонтов</span>
              </div>
              <div>
                <span class="font-weight-bold">{{ repair_types_count }}</span>
              </div>
            </div>

            <div
              v-for="item in repair_types"
              :key="item.title"
              class="data_item"
            >
              <div :class="$style.leads_discription_items">
                <div :class="$style.leads_discription_item_i">
                  <div
                    :class="[$style.leads_discription_label_color]"
                    :style="{ backgroundColor: colors[repair_types.indexOf(item)] }"
                  />
                  <span class="ml-4">{{ item.title }}</span>
                </div>
                <div>
                  <span class="headline font-weight-bold">{{ item.value }} ({{ item.percent }}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script lang="ts">
import {
  Vue, Component, Prop, Emit,
} from 'vue-property-decorator';
import { widgets_store, analytics_store } from '@/store';
import { Select, DatepickerRange } from '@/components/global';
import { AnalyticsFilters } from '@/modules/analytics/types';

const { state } = widgets_store;

const analytics_state = analytics_store.state;

@Component({
  components: {
    apexchart: () => import('vue-apexcharts'),
    Select,
    DatepickerRange,
  },
})
export default class WidgetRepairsRatio extends Vue {
  @Prop({ type: Boolean, default: false })
  is_filters!: boolean;

  @Emit()
  filter_update(key: keyof AnalyticsFilters, value: unknown, section: string) {
    analytics_store.mutations.UPD_FILTER_KEY({ key, value, section });
  }

  get repair_types_loading() {
    return analytics_state.repair_types_loading;
  }

  get filters() {
    return analytics_state.filters;
  }

  get clients() {
    return analytics_state.clients;
  }

  get orgs() {
    return analytics_state.organizations;
  }

  get analytics_repair_types() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.repair_types.items
      : analytics_state.analytics.repair_types.items;
  }

  get analytics_repair_types_count() {
    const response_filters = this.has_response_filters();

    return response_filters
      ? analytics_state.response_filters.repair_types.total_count
      : analytics_state.analytics.repair_types.total_count;
  }

  get repair_types() {
    return state.widgets.repair_types?.items;
  }

  get repair_types_count() {
    return state.widgets.repair_types?.total_count;
  }

  has_response_filters() {
    return (
      analytics_state.filters.repair_types_dates.length > 0
      || analytics_state.filters.repair_types_organization_id.length > 0
      || analytics_state.filters.repair_types_client_id !== null
    );
  }

  labels() {
    let result: string[] = [];

    if (this.is_filters) {
      result = this.analytics_repair_types ? this.analytics_repair_types?.map((product) => product.title) : [];
    } else {
      result = this.repair_types ? this.repair_types.map((product) => product.title) : [];
    }
    return result;
  }

  colors = [
    '#083DC7', '#477DE4', '#9BB2EC', '#C1CDF0', '#e1e4ed',
  ]

  get chartOptions() {
    const labels = this.labels();

    return {
      chart: {
        type: 'donut',
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '76%',
          },
        },
      },
      labels,
      dataLabels: {
        enabled: false,
      },
      colors: [
        '#083DC7', '#477DE4', '#9BB2EC', '#C1CDF0', '#e1e4ed',
      ],
      legend: {
        show: false,
      },
      tooltip: {
        enabled: true,
      },
    };
  }

  get series() {
    if (this.is_filters) {
      return this.analytics_repair_types?.map((item) => item.value);
    }
    return this.repair_types?.map((item) => item.value);
  }

  handle_change_dates(dates: string[]) {
    const dates_unformatted = [...dates];
    const dates_formatted = dates_unformatted.map((date) => new Date(date));

    if (dates_formatted[1] < dates_formatted[0]) {
      [
        dates_unformatted[0], dates_unformatted[1],
      ] = [
        dates[1], dates[0],
      ];
    }
    this.filter_update('repair_types_dates', dates_unformatted, 'repair_types');
  }
}

</script>

<style module lang="scss">
@import '@/scss/common/filter-blocks.scss';

.orders_widget {
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 24px;
}
.widget_box {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.widget_title {
  font-style: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #13141C;
}
.widget_item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
}
.chart_container {
  width: 100%;
}
.leads_discription_items {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin: 16px 0;
}
.leads_discription_item_i {
  display: flex;
  justify-content: space-between;

}
.leads_discription_label_color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}
.donut_size {
  width: 400px;
  height: 312px;
}
.filterField {
  max-width: 100% !important;
}
.datepickerMargin {
  margin-bottom: -14px;
}

@media (min-width: 960px) and (max-width: 1439px) {
  .donut_size {
  width: 360px;
  height: 312px;
}
}
</style>
